// P1 — Pantalla de planta (rol `pantalla`, kiosco TV fullscreen).
// Spec: docs/spec-mvp/roles/07-pantalla.md. Mockups del responsable 2026-08-20.
//
// - Tema según config_pantalla.tema (pedido UPG 2026-08-25: en planta no se
//   sabe cuál se lee mejor con la luz ambiente). Default oscuro (marca §3);
//   se restaura el tema previo del dispositivo al salir.
// - Rotación automática de paneles según config_pantalla (el coordinador la
//   edita en C15 y la TV la refleja en ≤60 s vía polling).
// - Teclas 1-9 y ←/→ navegan manualmente (pausan el auto por dos ciclos).
// - Modo andon (ajustado 2026-08-20 con datos reales de Sopó — media planta
//   podía estar <60% y la alerta secuestraba la TV para siempre):
//     · PARO abierto ≥ N min → TOMA la pantalla (urgencia real).
//     · Línea activa < 60%  → la alerta entra como PRIMER PANEL de la rotación,
//       sin congelar la TV.
//     · La navegación manual (pestañas/teclas) SIEMPRE gana por dos ciclos,
//       incluso sobre el paro — sin esto la vista previa de C15 era inusable.
// - Vista previa: coordinador/directivo/super_admin abren /pantalla?planta_id=X
//   con su PROPIA sesión (su scope contiene el de la pantalla; sin suplantación).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { PantallaDatos } from 'schemas';
import { useAuth } from '../../lib/auth';
import { usePantallaDatos } from './queries';
import { useRotacion } from './useRotacion';
import { useSalidaKiosco, TOQUES_PARA_SALIR } from './useSalidaKiosco';
import { colorPct, ETIQUETA, fechaLargaPantalla } from './shared';
import { PanelLideres } from './paneles/PanelLideres';
import { PanelPodio } from './paneles/PanelPodio';
import { PanelSupervisores } from './paneles/PanelSupervisores';
import { PanelLineas } from './paneles/PanelLineas';
import { PanelTurno } from './paneles/PanelTurno';
import { PanelPits } from './paneles/PanelPits';
import { PanelAndon, type EventoAndon } from './paneles/PanelAndon';

const NOMBRES_PANEL: Record<string, string> = {
  andon: 'Alerta',
  lideres: 'Líderes',
  podio: 'Podio',
  supervisores: 'Supervisores',
  lineas: 'Líneas',
  turno: 'Turno',
  pits: 'Pits',
};

/** Minutos desde medianoche de un "HH:MM[:SS]". */
function minutosDe(hhmm: string): number {
  const [h = 0, m = 0, s = 0] = hhmm.split(':').map(Number);
  return h * 60 + m + s / 60;
}

/** % transcurrido del turno con el reloj vivo (linealiza el cruce de medianoche). */
function pctTurno(horaInicio: string, horaFin: string, ahora: Date): number {
  const ini = minutosDe(horaInicio);
  let fin = minutosDe(horaFin);
  let act = ahora.getHours() * 60 + ahora.getMinutes() + ahora.getSeconds() / 60;
  if (fin <= ini) fin += 24 * 60;
  if (act < ini) act += 24 * 60;
  return Math.min(100, Math.max(0, ((act - ini) / (fin - ini)) * 100));
}

export function PantallaPage() {
  const { state, signOut } = useAuth();
  const salida = useSalidaKiosco();
  const [search] = useSearchParams();
  const plantaParam = search.get('planta_id');
  const esPreview = state.kind === 'signed_in' && state.claims.rol !== 'pantalla';

  const { data, error, dataUpdatedAt } = usePantallaDatos(esPreview ? plantaParam : null);

  // Tema de la TV según config (oscuro mientras carga). El tema ORIGINAL del
  // dispositivo se captura una sola vez: si el efecto re-corre porque el
  // coordinador cambió el tema, "lo previo" ya sería lo que puso la pantalla.
  const temaConfig = data?.config.tema ?? 'oscuro';
  const temaOriginal = useRef<string | null>(null);
  const originalCapturado = useRef(false);
  useEffect(() => {
    if (!originalCapturado.current) {
      temaOriginal.current = document.documentElement.getAttribute('data-theme');
      originalCapturado.current = true;
    }
    document.documentElement.setAttribute('data-theme', temaConfig === 'claro' ? 'light' : 'dark');
  }, [temaConfig]);
  useEffect(
    () => () => {
      const previo = temaOriginal.current;
      if (previo) document.documentElement.setAttribute('data-theme', previo);
      else document.documentElement.removeAttribute('data-theme');
    },
    [],
  );

  // Reloj vivo del TENANT: base = hora_tenant del server (pared local sin zona,
  // el parser la toma como local del dispositivo — solo sirve de ancla) + lo
  // transcurrido desde esa respuesta. Nunca el reloj del dispositivo directo.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, []);
  const ahora = useMemo(() => {
    if (!data) return null;
    const base = new Date(data.hora_tenant);
    return new Date(base.getTime() + (Date.now() - dataUpdatedAt));
    // tick fuerza el recálculo cada segundo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dataUpdatedAt, tick]);

  // ── Andon ──
  const eventosAndon: EventoAndon[] = useMemo(() => {
    if (!data || !data.config.andon_habilitado || !ahora) return [];
    const transcurrido = Math.floor((Date.now() - dataUpdatedAt) / 60_000);
    const paros: EventoAndon[] = data.paros_abiertos
      .map((p) => ({ tipo: 'paro' as const, paro: p, minutos: p.minutos_abierto + transcurrido }))
      .filter((e) => e.minutos >= data.config.andon_min_paro)
      .sort((a, b) => b.minutos - a.minutos);
    const lineas: EventoAndon[] = data.lineas
      .filter((l) => l.estado !== 'sin_actividad' && (l.cumplimiento_pct ?? 100) < 60)
      .map((l) => ({ tipo: 'linea' as const, linea: l }));
    return [...paros, ...lineas];
  }, [data, ahora, dataUpdatedAt]);
  const hayParoLargo = eventosAndon.some((e) => e.tipo === 'paro');

  // ── Rotación ──
  // Con eventos de andon, la alerta entra como PRIMER panel de la rotación.
  const paneles: string[] = useMemo(() => {
    if (!data) return [];
    const base = data.config.panel_fijo ? [data.config.panel_fijo] : data.config.paneles;
    return eventosAndon.length > 0 ? ['andon', ...base] : [...base];
  }, [data, eventosAndon.length]);
  const [auto, setAuto] = useState(true);
  const segundosRotacion = data?.config.segundos_rotacion ?? 30;

  // El motor de rotación vive en useRotacion (contrato de PRIMITIVOS: ver el
  // encabezado de ese archivo — colgarlo de `data` hacía que el polling
  // reiniciara el timer y la TV no rotara nunca).
  const {
    idx,
    ciclo,
    forzado: panelForzado,
    irA,
    forzar,
    enPausa: manualActivo,
  } = useRotacion({
    activo: !!data && auto,
    segundos: segundosRotacion,
    total: paneles.length,
  });

  // El PARO largo toma la pantalla (decisión de RENDER, no del timer: la
  // rotación sigue corriendo por debajo y retoma sola cuando el paro se
  // cierra). Una línea baja solo suma el panel de alerta a la rotación.
  const takeover = hayParoLargo && !manualActivo;

  // Teclado: 1-9 directo, ←/→ relativo. Las flechas y los números del control
  // remoto de un smart TV llegan como estas mismas teclas.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key >= '1' && e.key <= '9') irA(Number(e.key) - 1);
      else if (e.key === 'ArrowRight') irA(idx + 1);
      else if (e.key === 'ArrowLeft') irA(idx - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [irA, idx]);

  // Mensajes desde C15 (vista previa embebida): mostrar un panel puntual.
  // Solo mismo origen — la config del coordinador vive en la misma app.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const msg = e.data as { tipo?: string; panel?: string };
      if (msg?.tipo !== 'pantalla:mostrar' || !msg.panel) return;
      const i = paneles.indexOf(msg.panel);
      // Panel activo → se navega; desactivado → se fuerza igual, para que el
      // coordinador pueda verlo antes de decidir si lo prende.
      if (i >= 0) irA(i);
      else forzar(msg.panel);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [paneles, irA, forzar]);

  const panelActivo =
    panelForzado && manualActivo ? panelForzado : takeover ? 'andon' : (paneles[idx] ?? 'turno');

  if (error) {
    return (
      <Lienzo>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <Wordmark grande />
          <p className="max-w-xl text-lg text-fg-secondary">
            No fue posible cargar la pantalla de planta. Verifique que el usuario tenga una planta
            asignada y vuelva a intentarlo.
          </p>
        </div>
      </Lienzo>
    );
  }
  if (!data || !ahora) {
    return (
      <Lienzo>
        <div className="flex flex-1 flex-col items-center justify-center gap-5">
          <Wordmark grande />
          <p className="text-sm tracking-[0.24em] text-fg-tertiary">CONECTANDO CON LA PLANTA…</p>
        </div>
      </Lienzo>
    );
  }

  const baseLabel =
    data.config.base_cumplimiento === 'programa' ? 'programa del cliente' : 'capacidad instalada';

  return (
    <Lienzo>
      <style>{`
        @keyframes pantalla-panel-in {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pantalla-avance { from { width: 0% } to { width: 100% } }
      `}</style>

      {salida.abierto ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-8"
          style={{ background: 'rgba(0,0,0,0.72)' }}
          role="dialog"
          aria-modal="true"
          aria-label="Cerrar la sesión de la pantalla"
        >
          <div className="w-full max-w-lg rounded-xl border border-border-strong bg-surface-elevated p-8 text-center shadow-floating">
            <p className="font-serif text-[1.9rem] leading-tight text-fg">
              ¿Cerrar la sesión de esta pantalla?
            </p>
            <p className="mt-3 text-sm text-fg-secondary">
              La TV dejará de mostrar la producción hasta que alguien vuelva a iniciar sesión con la
              contraseña del usuario de pantalla.
            </p>
            <p className="mt-4 font-mono text-xs text-fg-tertiary">
              {state.kind === 'signed_in' ? state.claims.email : ''} · {data.planta.nombre}
            </p>
            <div className="mt-7 flex justify-center gap-3">
              <button
                type="button"
                onClick={salida.cancelar}
                className="rounded-md border border-border-strong px-6 py-3 text-sm text-fg"
                autoFocus
              >
                Seguir mostrando
              </button>
              <button
                type="button"
                onClick={() => void signOut()}
                className="rounded-md px-6 py-3 text-sm"
                style={{ background: 'var(--accent)', color: 'var(--accent-ink)' }}
              >
                Cerrar sesión
              </button>
            </div>
            <p className={`mt-5 ${ETIQUETA} text-fg-tertiary`}>
              Este aviso se cierra solo en unos segundos.
            </p>
          </div>
        </div>
      ) : null}

      <Header
        datos={data}
        ahora={ahora}
        esPreview={esPreview}
        baseLabel={baseLabel}
        salida={salida}
      />

      <main className="min-h-0 flex-1 overflow-hidden">
        <div
          key={panelActivo}
          className="h-full"
          style={{ animation: 'pantalla-panel-in 480ms cubic-bezier(0.22,1,0.36,1) both' }}
        >
          {panelActivo === 'andon' ? (
            <PanelAndon eventos={eventosAndon} />
          ) : panelActivo === 'lideres' ? (
            <PanelLideres datos={data} baseLabel={baseLabel} />
          ) : panelActivo === 'podio' ? (
            <PanelPodio datos={data} />
          ) : panelActivo === 'supervisores' ? (
            <PanelSupervisores datos={data} />
          ) : panelActivo === 'lineas' ? (
            <PanelLineas datos={data} />
          ) : panelActivo === 'pits' ? (
            <PanelPits datos={data} />
          ) : (
            <PanelTurno datos={data} baseLabel={baseLabel} />
          )}
        </div>
      </main>

      <footer className="flex h-[5.5vh] min-h-12 items-center justify-between gap-6 border-t border-border px-[4vw]">
        <div className="flex min-w-0 items-center gap-[2.5vw]">
          {paneles.map((p, i) => {
            const activo = panelActivo === p && (p !== 'andon' || takeover || i === idx);
            const esAlerta = p === 'andon';
            return (
              <button
                key={p}
                type="button"
                onClick={() => irA(i)}
                className="group flex flex-col items-start gap-1 py-1 text-left"
              >
                <span
                  className={`flex items-center gap-2 ${ETIQUETA} tracking-[0.14em] ${
                    activo ? 'text-fg' : 'text-fg-tertiary'
                  }`}
                  style={esAlerta ? { color: 'var(--crit)' } : undefined}
                >
                  <span
                    className={`inline-flex size-[1.5em] items-center justify-center rounded border ${
                      activo ? 'border-border-stronger bg-surface-elevated' : 'border-border'
                    }`}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {esAlerta ? '!' : i + 1}
                  </span>
                  {NOMBRES_PANEL[p]?.toUpperCase() ?? p.toUpperCase()}
                </span>
                <span className="h-0.5 w-full overflow-hidden rounded-full bg-surface-overlay">
                  {activo && auto && !data.config.panel_fijo ? (
                    <span
                      key={ciclo}
                      className="block h-full"
                      style={{
                        background: 'var(--fg)',
                        animation: `pantalla-avance ${data.config.segundos_rotacion}s linear both`,
                      }}
                    />
                  ) : activo ? (
                    <span className="block h-full w-full" style={{ background: 'var(--fg)' }} />
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex shrink-0 items-center gap-5">
          {takeover ? (
            <span className={`${ETIQUETA} tracking-[0.14em]`} style={{ color: 'var(--crit)' }}>
              ANDON ACTIVO
            </span>
          ) : data.config.panel_fijo ? (
            <span className={`${ETIQUETA} tracking-[0.14em] text-fg-tertiary`}>PANEL FIJO</span>
          ) : (
            <button
              type="button"
              onClick={() => setAuto((a) => !a)}
              className="flex items-center gap-2.5 rounded-full border border-border px-3.5 py-1.5"
              aria-pressed={auto}
            >
              <span
                className={`h-3.5 w-7 rounded-full transition-colors ${auto ? '' : 'bg-surface-overlay'}`}
                style={auto ? { background: 'var(--fg)' } : undefined}
                aria-hidden="true"
              />
              <span className={`whitespace-nowrap ${ETIQUETA} tracking-[0.14em] text-fg-secondary`}>
                AUTO
                {/* Las etiquetas escalan con el ancho, así que el pie nunca gana espacio:
                    el texto largo solo desde 1680 (a 1280 tapaba "7 PITS") y la ayuda
                    de teclas solo en pantallas de más de 1920 px CSS. */}
                <span className="hidden min-[1680px]:inline">
                  {' '}
                  · CAMBIA CADA {data.config.segundos_rotacion}S
                </span>
              </span>
            </button>
          )}
          <span
            className={`hidden ${ETIQUETA} tracking-[0.14em] text-fg-tertiary min-[2200px]:inline`}
          >
            ← → · TECLAS 1–{paneles.length}
          </span>
        </div>
      </footer>
    </Lienzo>
  );
}

// ─────────────────────────── piezas del marco ───────────────────────────

function Lienzo({ children }: { children: React.ReactNode }) {
  return (
    <div
      data-pantalla=""
      className="flex h-dvh w-full flex-col overflow-hidden bg-surface-base text-fg"
      // 14 px a 1280 de ancho (1.1vw), hasta 20 px en TV grande.
      style={{ '--pantalla-etiqueta': 'clamp(0.875rem, 1.1vw, 1.25rem)' } as React.CSSProperties}
    >
      {children}
    </div>
  );
}

function Wordmark({ grande = false }: { grande?: boolean }) {
  return (
    <span className={`font-serif ${grande ? 'text-5xl' : 'text-[1.35rem]'} leading-none text-fg`}>
      Jidokap<span style={{ color: 'var(--dot)' }}>.</span>
    </span>
  );
}

/** Planta y tenant bajo la marca: en dos renglones por debajo de 1800 px para no quitarle
 *  ancho al turno; en una sola línea en TV grande. */
function SubtituloPlanta({ planta, tenant }: { planta: string; tenant: string }) {
  return (
    <p
      className={`mt-1 whitespace-nowrap ${ETIQUETA} leading-snug tracking-[0.08em] text-fg-tertiary`}
    >
      <span className="block min-[1800px]:inline">{planta.toUpperCase()}</span>
      <span className="hidden min-[1800px]:inline"> · </span>
      <span className="block min-[1800px]:inline">{tenant.toUpperCase()}</span>
    </p>
  );
}

function Header({
  datos,
  ahora,
  esPreview,
  baseLabel,
  salida,
}: {
  datos: PantallaDatos;
  ahora: Date;
  esPreview: boolean;
  baseLabel: string;
  salida: { progreso: number; toque: () => void };
}) {
  const turno = datos.turno;
  const pct = turno ? pctTurno(turno.hora_inicio, turno.hora_fin, ahora) : null;
  const hh = String(ahora.getHours()).padStart(2, '0');
  const mm = String(ahora.getMinutes()).padStart(2, '0');
  const ss = String(ahora.getSeconds()).padStart(2, '0');
  const fechaLarga = fechaLargaPantalla(ahora);

  return (
    <header className="flex h-[11vh] min-h-20 items-center gap-[2vw] overflow-hidden border-b border-border bg-surface-elevated px-[2vw]">
      <div className="flex shrink-0 items-center gap-4">
        {/* El monograma es decorativo: se oculta en anchos chicos (iframe de la
            vista previa). La PUERTA DE SALIDA no vive acá — va sobre la marca,
            que está visible en todos los tamaños. */}
        <span
          className="hidden h-12 w-12 items-center justify-center rounded-lg bg-surface-base font-serif text-2xl text-fg ring-1 ring-border-strong lg:flex"
          aria-hidden="true"
        >
          J
        </span>
        {/* Puerta de salida del kiosco: N toques seguidos sobre la marca abren
            el diálogo de cerrar sesión (ver useSalidaKiosco). Enfocable → sirve
            el OK del control remoto. Solo para el rol pantalla: en vista previa
            cerraría la sesión del coordinador/directivo, que ya tiene "SALIR". */}
        {esPreview ? (
          <div>
            <Wordmark />
            <SubtituloPlanta planta={datos.planta.nombre} tenant={datos.tenant_nombre} />
          </div>
        ) : (
          <button
            type="button"
            onClick={salida.toque}
            aria-label={`Opciones de la pantalla (${TOQUES_PARA_SALIR} toques seguidos)`}
            className="relative block text-left"
          >
            <Wordmark />
            <SubtituloPlanta planta={datos.planta.nombre} tenant={datos.tenant_nombre} />
            {salida.progreso > 0 ? (
              <span
                className="absolute -bottom-1 left-0 h-0.5 rounded-full transition-[width]"
                style={{
                  width: `${(salida.progreso / TOQUES_PARA_SALIR) * 100}%`,
                  background: 'var(--accent)',
                }}
                aria-hidden="true"
              />
            ) : null}
          </button>
        )}
        {esPreview ? (
          <span className="ml-2 flex items-center gap-2">
            <span
              className="rounded border border-border-strong px-2 py-1 text-[0.62rem] tracking-[0.18em] text-fg-secondary"
              title="Está viendo la pantalla con su propia sesión"
            >
              VISTA PREVIA
            </span>
            {/* Salida visible: sin esto, entrar desde el menú del directivo
                dejaba un kiosco fullscreen sin retorno más que el back del
                navegador. Oculta dentro del iframe de C15 (ya está en su
                propia pantalla de configuración). */}
            {window.self === window.top ? (
              <button
                type="button"
                onClick={() => window.history.back()}
                className="rounded border border-border px-2 py-1 text-[0.62rem] tracking-[0.18em] text-fg-tertiary hover:text-fg"
              >
                ← SALIR
              </button>
            ) : null}
          </span>
        ) : null}
      </div>

      <div className="h-[55%] w-px shrink-0 bg-border" aria-hidden="true" />

      <div className="min-w-0 flex-1 overflow-hidden">
        {turno ? (
          <>
            <div className="flex items-baseline justify-between gap-4">
              <p className="min-w-0 truncate whitespace-nowrap text-[clamp(0.9rem,1.3vw,1.4rem)] font-medium text-fg">
                {turno.nombre}
                <span
                  className="ml-3 font-mono text-[0.85em] text-fg-tertiary"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {turno.hora_inicio.slice(0, 5)} – {turno.hora_fin.slice(0, 5)}
                </span>
              </p>
              <p
                className={`hidden shrink-0 whitespace-nowrap ${ETIQUETA} tracking-[0.14em] text-fg-tertiary lg:block`}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {Math.round(pct ?? 0)}% DEL TURNO
              </p>
            </div>
            <div className="mt-2 h-1.5 w-full rounded-full bg-surface-overlay">
              <div
                className="h-full rounded-full transition-[width] duration-1000 ease-linear"
                style={{ width: `${pct ?? 0}%`, background: 'var(--fg)' }}
              />
            </div>
          </>
        ) : (
          <p className="text-[clamp(0.95rem,1.2vw,1.3rem)] text-fg-tertiary">
            Sin turno en curso · mostrando la jornada operativa acumulada
          </p>
        )}
      </div>

      <div className="h-[55%] w-px shrink-0 bg-border" aria-hidden="true" />

      <div className="shrink-0 text-right">
        <p className={`whitespace-nowrap ${ETIQUETA} tracking-[0.14em] text-fg-tertiary`}>
          CUMPL. PLANTA
          <span className="hidden normal-case tracking-normal lg:block">vs {baseLabel}</span>
        </p>
        <p
          className="mt-0.5 text-[clamp(1.3rem,2.6vw,2.9rem)] font-medium leading-none"
          style={{
            color: colorPct(datos.resumen.cumplimiento_pct),
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {datos.resumen.cumplimiento_pct === null
            ? '—'
            : Math.round(datos.resumen.cumplimiento_pct)}
          <span className="text-[0.5em] text-fg-secondary">%</span>
        </p>
      </div>

      <div className="h-[55%] w-px shrink-0 bg-border" aria-hidden="true" />

      <div className="shrink-0 text-right">
        <p
          className="whitespace-nowrap text-[clamp(1.4rem,3vw,3.2rem)] font-medium leading-none text-fg"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {hh}
          <span className="animate-pulse">:</span>
          {mm}
          <span className="text-[0.5em] text-fg-tertiary">:{ss}</span>
        </p>
        <p className={`mt-1 hidden whitespace-nowrap ${ETIQUETA} text-fg-secondary md:block`}>
          {fechaLarga}
        </p>
      </div>
    </header>
  );
}
